import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ChevronLeft, Image, X, MapPin, Calendar, 
  Eye, AlertTriangle, CheckCircle, User
} from 'lucide-react';
import axios from 'axios';
import { MathCaptcha } from '../components/MathCaptcha';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function SubmitSightingPage() {
  const { caseId } = useParams();
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  
  const [formData, setFormData] = useState({
    case_id: caseId,
    description: '',
    location: {
      address: '',
      city: '',
      state: '',
      country: '',
      latitude: null,
      longitude: null
    },
    sighting_date: '',
    sighting_time: '',
    is_anonymous: false
  });

  useEffect(() => {
    fetchCaseData();
    // Try to get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            location: {
              ...prev.location,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            }
          }));
        },
        () => {} // Ignore errors
      );
    }
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(response.data);
    } catch (error) {
      console.error('Failed to fetch case:', error);
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaVerified) {
      setError('Please complete the security check before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        ...formData,
        photos
      };

      const headers = {};
      if (token && !formData.is_anonymous) {
        headers.Authorization = `Bearer ${token}`;
      }

      await axios.post(`${API}/sightings`, payload, {
        withCredentials: !formData.is_anonymous,
        headers
      });

      setSuccess(true);
      setTimeout(() => navigate(`/case/${caseId}`), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit sighting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Sighting Reported</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for your help! Your sighting report has been submitted and will be reviewed.
            </p>
            <p className="text-sm text-muted-foreground">Redirecting to case details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to={`/case/${caseId}`} className="text-muted-foreground hover:text-primary">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
                <span className="text-lg font-black text-slate-900">MISYOU</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {/* Case Summary */}
        {caseData && (
          <Card className="mb-6 bg-white shadow-lg">
            <CardContent className="p-4 flex items-center gap-4">
              {caseData.photos && caseData.photos.length > 0 ? (
                <img 
                  src={caseData.photos[0]} 
                  alt={caseData.full_name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center">
                  <User className="w-8 h-8 text-slate-400" />
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Reporting sighting for</p>
                <h2 className="text-xl font-bold text-slate-900">{caseData.full_name}</h2>
                {caseData.last_seen_location?.city && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Last seen: {caseData.last_seen_location.city}, {caseData.last_seen_location.state}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Card className="shadow-xl" data-testid="sighting-form">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Report a Sighting</CardTitle>
                  <CardDescription>Your information could help bring them home</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Photo Upload */}
              <div>
                <Label className="mb-3 block">Photos (Optional but helpful)</Label>
                <div className="flex flex-wrap gap-4 mb-3">
                  {photos.map((photo, index) => (
                    <div key={photo.slice(0, 64)} className="relative w-24 h-24">
                      <img 
                        src={photo} 
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                    <Image className="w-6 h-6 text-slate-400" />
                    <span className="text-xs text-muted-foreground mt-1">Add Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handlePhotoUpload}
                      data-testid="sighting-photo-upload"
                    />
                  </label>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">What did you see? *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe where and when you saw this person, what they were doing, who they were with, etc."
                  className="mt-1"
                  rows={5}
                  required
                  data-testid="sighting-description"
                />
              </div>

              {/* Location */}
              <div>
                <Label className="mb-3 block">Where did you see them?</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      value={formData.location.address}
                      onChange={(e) => handleChange('location.address', e.target.value)}
                      placeholder="Street address or landmark"
                      data-testid="sighting-address"
                    />
                  </div>
                  <div>
                    <Input
                      value={formData.location.city}
                      onChange={(e) => handleChange('location.city', e.target.value)}
                      placeholder="City"
                      data-testid="sighting-city"
                    />
                  </div>
                  <div>
                    <Input
                      value={formData.location.state}
                      onChange={(e) => handleChange('location.state', e.target.value)}
                      placeholder="State/Province"
                      data-testid="sighting-state"
                    />
                  </div>
                </div>
                {formData.location.latitude && (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    GPS location captured automatically
                  </p>
                )}
              </div>

              {/* Date/Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Date of Sighting</Label>
                  <Input
                    type="date"
                    value={formData.sighting_date}
                    onChange={(e) => handleChange('sighting_date', e.target.value)}
                    className="mt-1"
                    data-testid="sighting-date"
                  />
                </div>
                <div>
                  <Label>Time of Sighting</Label>
                  <Input
                    type="time"
                    value={formData.sighting_time}
                    onChange={(e) => handleChange('sighting_time', e.target.value)}
                    className="mt-1"
                    data-testid="sighting-time"
                  />
                </div>
              </div>

              {/* Anonymous Option */}
              <div className="pt-4 border-t border-slate-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={formData.is_anonymous}
                    onCheckedChange={(v) => handleChange('is_anonymous', v)}
                    className="mt-0.5"
                    data-testid="sighting-anonymous"
                  />
                  <div>
                    <span className="font-medium text-slate-900">Submit Anonymously</span>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your identity will not be shared with anyone. You can still provide accurate 
                      information without revealing who you are.
                    </p>
                  </div>
                </label>
              </div>

              {/* Math CAPTCHA — anti-spam */}
              <MathCaptcha onVerify={setCaptchaVerified} />

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={submitting || !formData.description || !captchaVerified}
                className="w-full h-12 bg-red-600 hover:bg-red-700 rounded-xl text-lg btn-press"
                data-testid="submit-sighting-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Sighting Report'}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                Your report will be reviewed and added to the case file. Thank you for helping!
              </p>
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
  );
}
